import { useState, useEffect, useContext } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { ModalKey, ModalContext } from 'components/Modals/ModalContext'
import { useGetCoaches } from 'redux/customHooks/useGetCoaches'
import { GetRoles, ConvertRoleToDisplayName } from 'functions/userUtils'

import ScreenFrame from 'components/Frame/ScreenFrame'
import SearchBar from 'components/Search/SearchBar'
import BrandColorButton from 'components/Buttons/BrandColorButton'
import PaginationController from 'components/Pagination/PaginationController'
import ContentFrame from 'components/Frame/ContentFrame'
import CoachProfileListItem from 'components/Coach/CoachProfileListItem'

export default function CoachList() {
    const navigate = useNavigate()

    const { ShowModal } = useContext(ModalContext)
    
    const coaches = useGetCoaches()

    const requirePaymentMethodToInviteAthletes = useSelector((state) => state.user.requirePaymentMethodToInviteAthletes)
    const organizationAdminStripeCustomerId = useSelector((state) => state.user.organizationAdminStripeCustomerId)
    const isTrainingPeaksIntegrationActive = useSelector((state) => state.user.isTrainingPeaksIntegrationActive)
    
    const [searchTerm, setSearchTerm] = useState('')
    const [showFilters, setShowFilters] = useState(false)
    const [roleFilter, setRoleFilter] = useState('')
    const [trainingPeaksIntegrationStatusFilter, setTrainingPeaksIntegrationStatusFilter] = useState('')
    const [filteredCoaches, setFilteredCoaches] = useState([])
    const [paginatedCoaches, setPaginatedCoaches] = useState([])
    const [pagination, setPagination] = useState({
        page: 1,
        pageSize: 10,
        totalCount: 0,
        totalPages: 0,
        hasNext: false,
        hasPrevious: false
    })

    const OnAddCoaches = () => {
        if (HasOrganizationPaymentMethodBeenAdded() === false) {
            ShowPaymentMethodRequiredWarning()
            return
        }

        navigate('/onboarding/coaches/options')
    }

    const HasOrganizationPaymentMethodBeenAdded = () => {

        if (requirePaymentMethodToInviteAthletes === 0) { 
            return true
        }

        if (requirePaymentMethodToInviteAthletes === 1) {

            if (organizationAdminStripeCustomerId === '') {
                return false
            }

            return true
        }
    }

    const ShowPaymentMethodRequiredWarning = () => {
        const modalData = {
            key: ModalKey.ActionWarning,
            size: 'quarter',
            titleText: 'Unable to add coaches',
            bodyText: [
                'In order to add additional coaches a payment method must be added to the organization.',
                'A payment method can be added by the organization owner'
            ],
            buttonText: 'Add payment method',
            buttonAction: () => navigate('/settings/billing')
        }

        ShowModal(modalData)
    }
    
    const FilterCoachesBySearchTerm = (item) => {
        const fullName = item.firstName.toLowerCase() + ' ' + item.lastName.toLowerCase()

        return (
            fullName.includes(searchTerm.toLowerCase()) ||
            item.email.toLowerCase().includes(searchTerm.toLowerCase())
        )
    }

    const FilterCoachesByRole = (item) => {
        if (roleFilter === '') return true
        return item.type === roleFilter
    }

    const FilterCoachesByTrainingPeaksIntegrationStatus = (coach) => {
        if (trainingPeaksIntegrationStatusFilter === '') return true
        if (trainingPeaksIntegrationStatusFilter === 'active') return coach.isTrainingPeaksIntegrationActive
        if (trainingPeaksIntegrationStatusFilter === 'inactive') return !coach.isTrainingPeaksIntegrationActive
        return true
    }

    const HandlePageSizeChange = (newPageSize) => {
        setPagination(prev => ({
            ...prev,
            pageSize: parseInt(newPageSize),
            page: 1
        }))
    }

    // Update filtered athletes when search term or coach filter changes
    useEffect(() => {
        if (coaches !== null) {
            const filtered = coaches.filter(coach => 
                FilterCoachesBySearchTerm(coach) 
                && FilterCoachesByRole(coach)
                && FilterCoachesByTrainingPeaksIntegrationStatus(coach)
            )

            setFilteredCoaches(filtered)
        }
    }, [coaches, searchTerm, roleFilter, trainingPeaksIntegrationStatusFilter])

    // Update pagination and paginated athletes when filteredAthletes or pagination values change
    useEffect(() => {
        if (filteredCoaches.length > 0) {
            const totalCount = filteredCoaches.length
            const totalPages = Math.ceil(totalCount / pagination.pageSize) || 1
            const currentPage = Math.min(pagination.page, totalPages || 1)
            
            // Update pagination
            setPagination(prev => ({
                ...prev,
                totalCount,
                totalPages,
                page: currentPage,
                hasNext: currentPage < totalPages,
                hasPrevious: currentPage > 1
            }))

            // Update paginated athletes
            const startIndex = (pagination.page - 1) * pagination.pageSize
            const endIndex = startIndex + pagination.pageSize

            setPaginatedCoaches(filteredCoaches.slice(startIndex, endIndex))
        } else {
            setPagination(prev => ({
                ...prev,
                totalCount: 0,
                totalPages: 0,
                page: 1,
                hasNext: false,
                hasPrevious: false
            }))

            setPaginatedCoaches([])
        }
    }, [filteredCoaches.length, pagination.page, pagination.pageSize])

    if (coaches === null) { 
        return (
            <ScreenFrame>
                <div className='w-full h-20 bg-gray-200 rounded-lg animate-pulse' />
                <div className='w-full h-[800px] bg-gray-200 rounded-lg animate-pulse' />
            </ScreenFrame>
        )
    }

    return (
        <ScreenFrame>
            <div className='flex items-center gap-x-4'>
                <h1 className="text-2xl font-semibold text-gray-900 tracking-tight pt-2 flex-grow">
                    Coaches
                </h1>

                {isTrainingPeaksIntegrationActive &&
                    <div 
                        className="h-10 bg-white flex items-center justify-center gap-x-2 rounded-2xl px-10 cursor-pointer hover:bg-gray-50 transition-colors shadow-md"
                        onClick={() => navigate('/settings/integrations')}
                    >
                        <p>Manage TrainingPeaks Integration</p>
                    </div>
                }

                <div className="w-40">
                    <BrandColorButton 
                        text="Add Coaches" 
                        action={OnAddCoaches}
                        className='shadow-md'
                    />
                </div>
            </div>

            <SearchBar 
                placeholder="Enter Coach Name or Email"
                searchTerm={searchTerm} 
                setSearchTerm={setSearchTerm} 
                showFilters={showFilters} 
                setShowFilters={setShowFilters} 
            />

            {/* Filters */}
            <div>
                <div
                    className='-mb-4 flex gap-4 transition-all duration-200 select-none'
                    style={{
                        maxHeight: showFilters ? '100px' : '0px',
                        opacity: showFilters ? 1 : 0,
                        paddingBottom: showFilters ? '1rem' : '0',
                        pointerEvents: showFilters ? 'auto' : 'none'
                    }}
                >
                    {/* Items per page */}
                    <div className='flex items-center gap-x-1'>
                        <p className='text-sm text-gray-700 '>Items per page:</p>

                        <div className="border border-gray-200 rounded-md shadow-md">
                            <select
                                value={pagination.pageSize}
                                className="px-2 py-1 text-sm border-r-[10px] border-white rounded-md cursor-pointer focus:outline-none"
                                onChange={(e) => HandlePageSizeChange(e.target.value)}
                            >
                                <option value="10">10</option>
                                <option value="25">25</option>
                                <option value="50">50</option>
                                <option value="100">100</option>
                            </select>
                        </div>
                    </div>

                    {/* Coach Role */}
                    <div className='flex items-center gap-x-1'>
                        <p className='text-sm text-gray-700'>Role:</p>

                        <div className="border border-gray-200 rounded-md shadow-md">
                            <select
                                value={roleFilter}
                                className="px-2 py-1 text-sm border-r-[10px] border-white rounded-md cursor-pointer focus:outline-none"
                                onChange={(e) => setRoleFilter(e.target.value)}
                            >
                                <option value="">All</option>
                                {GetRoles().map((role, index) => (
                                    <option key={index} value={role}>{ConvertRoleToDisplayName(role)}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* TrainingPeaks Integration Status */}
                    <div className='flex items-center gap-x-1'>
                        <p className='text-sm text-gray-700'>TrainingPeaks Integration Status:</p>
                        
                        <div className="border border-gray-200 rounded-md shadow-md">
                            <select
                                value={trainingPeaksIntegrationStatusFilter}
                                className="px-2 py-1 text-sm border-r-[10px] border-white rounded-md cursor-pointer focus:outline-none"
                                onChange={(e) => setTrainingPeaksIntegrationStatusFilter(e.target.value)}
                            >
                                <option value="">All</option>
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                            </select>
                        </div>
                    </div>

                </div>
            </div>

            <PaginationController pagination={pagination} setPagination={setPagination} />

            <ContentFrame className='divide-y divide-gray-200'>
                {paginatedCoaches.length === 0 &&
                    <div className="text-center text-gray-500 py-4">No athletes found</div>
                }

                {paginatedCoaches.length > 0 && paginatedCoaches.map((coach, index) => (
                    <CoachProfileListItem key={index} coach={coach} />
                ))}
            </ContentFrame>

            <PaginationController pagination={pagination} setPagination={setPagination} />
        </ScreenFrame>
    )
}
